import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import CssBaseline from "@mui/material/CssBaseline";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ErrorBoundary } from "react-error-boundary";
import {
  ActiveFarmProvider,
  AlertProvider,
  ApiProvider,
  ConfigProvider,
  useConfigContext,
} from "@/contexts";
import { ThemeProvider } from "@mui/material/styles";
import { BrowserRouter as Router } from "react-router-dom";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import ErrorFallback from "@/components/common/ErrorFallback";
import { Auth0ProviderWithConfig } from "@/components/providers/Auth0ProviderWithConfig";
import PageLoading from "@/pages/loading";
import theme from "@/config/theme";
import "moment/locale/en-gb";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

// eslint-disable-next-line react-refresh/only-export-components
function AppBootstrap() {
  const { loading, error, retryConfig } = useConfigContext();

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 600 }}>
          <Alert
            severity="error"
            action={
              error.canRetry && (
                <Button color="inherit" size="small" onClick={retryConfig}>
                  Retry
                </Button>
              )
            }
          >
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {error.message}
            </Typography>
            <Typography variant="body1">
              Please retry and contact the Agribot team if you experience more
              issues.
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return <PageLoading loadingText="Loading application configuration..." />;
  }

  return (
    <Auth0ProviderWithConfig>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={"en-gb"}
        >
          <AlertProvider>
            <ActiveFarmProvider>
              <ApiProvider>
                <Router>
                  <App />
                </Router>
              </ApiProvider>
            </ActiveFarmProvider>
          </AlertProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Auth0ProviderWithConfig>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onReset={() => window.location.assign("/")}
  >
    <ConfigProvider>
      <AppBootstrap />
    </ConfigProvider>
  </ErrorBoundary>
);

reportWebVitals();
